/* eslint-disable complexity */
import type { Community } from '@communities/community/domain';
import { useCreateCommunity, useUpdateCommunity } from '@communities/community/hooks';
import { ErrorBanner } from '@core/components';
import type { DomainError } from '@core/domain';
import { Dialog, LoadingOverlay, LoadingSpinner, TextArea } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

import * as styles from './CommunityDialog.css';
import { BasicInfoSection } from './CommunityDialogBasicInfo';
import { ContentInfoSection } from './CommunityDialogContentInfo';
import { StoreIntegrationSection } from './CommunityDialogStoreIntegration';

interface CommunityDialogProps {
  isOpen: boolean;
  onClose: () => void;
  groupId?: string;
  initialCommunity?: Community | undefined;
  onPromptWarnings?: (warnings: DomainError[]) => void;
}

/**
 * Implementation of the Community Dialog
 * Supports both creating new communities and editing existing ones
 */
export function CommunityDialog({ isOpen, onClose, groupId, initialCommunity, onPromptWarnings }: CommunityDialogProps) {
  const [name, setName] = useState<string>(initialCommunity?.name || '');
  const [description, setDescription] = useState<string>(initialCommunity?.optionalMetadata?.description || '');
  const [bigId, setBigId] = useState<string>(initialCommunity?.optionalMetadata?.bigId || '');
  const [contentGenres, setContentGenres] = useState<string[]>(initialCommunity?.optionalMetadata?.contentGenres || []);
  const [contentStandardsUrl, setContentStandardsUrl] = useState<string>(initialCommunity?.optionalMetadata?.contentStandardsUrl || '');
  const [pgRating, setPgRating] = useState<string>(initialCommunity?.optionalMetadata?.pgRating || 'General');

  const { createCommunity, isCreating, isGenerating: isCreatingGenerating } = useCreateCommunity({ onPromptWarnings });
  const { updateCommunity, isUpdating, isGenerating: isUpdatingGenerating, errors: updateErrors } = useUpdateCommunity();

  const isGeneratingPrompt = isCreatingGenerating || isUpdatingGenerating;
  const isSubmitting = isCreating || isUpdating || isGeneratingPrompt;

  // Reinitialize form state when dialog opens or initialCommunity changes
  useEffect(() => {
    if (isOpen) {
      setName(initialCommunity?.name || '');
      setDescription(initialCommunity?.optionalMetadata?.description || '');
      setBigId(initialCommunity?.optionalMetadata?.bigId || '');
      setContentGenres(initialCommunity?.optionalMetadata?.contentGenres || []);
      setContentStandardsUrl(initialCommunity?.optionalMetadata?.contentStandardsUrl || '');
      setPgRating(initialCommunity?.optionalMetadata?.pgRating || 'General');
    }
  }, [isOpen, initialCommunity]);

  // Form validation
  const isFormValid = name.trim() !== '' && description.trim() !== '';

  // Handle form submission
  const handleSubmit = async () => {
    const input = {
      name,
      optionalMetadata: {
        description,
        bigId,
        contentGenres,
        contentStandardsUrl,
        pgRating,
      },
    };

    if (initialCommunity && initialCommunity.id) {
      await updateCommunity(initialCommunity.id, input);
      onClose();
    } else {
      // For creating a new community - prompt generation is handled by the hook
      await createCommunity(groupId || '', input);
      onClose();
    }
  };

  if (isSubmitting) {
    const message = isGeneratingPrompt ? 'Generating AI prompts for community...' : isCreating ? 'Creating community...' : 'Updating community...';

    return <LoadingOverlay fullScreen message={message} spinnerSize="large" />;
  }

  return (
    <Dialog
      cancelAction={{
        label: 'Cancel',
        disabled: isSubmitting,
      }}
      description={initialCommunity ? 'Edit community settings' : 'Create a new community'}
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : initialCommunity ? 'Update Community' : 'Create Community',
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid,
        variant: 'ai',
      }}
      size="xxxl"
      title={initialCommunity ? 'Edit Community' : 'Add Community'}
      onClose={onClose}
    >
      {updateErrors && updateErrors.length > 0 && <ErrorBanner errors={updateErrors} title="There was an error updating the community:" />}

      <BasicInfoSection bigId={bigId || ''} disabled={isSubmitting} name={name} setBigId={value => setBigId(value)} setName={value => setName(value)} />

      <ContentInfoSection
        contentGenres={contentGenres || []}
        disabled={isSubmitting}
        pgRating={pgRating || 'General'}
        setContentGenres={value => setContentGenres(value)}
        setPgRating={value => setPgRating(value)}
      />

      <StoreIntegrationSection
        contentStandardsUrl={contentStandardsUrl || ''}
        disabled={isSubmitting}
        setContentStandardsUrl={value => setContentStandardsUrl(value)}
      />

      <div className={styles.formSection}>
        <TextArea
          required
          disabled={isSubmitting}
          id="communityDescription"
          placeholder="Enter community description"
          rows={10}
          size={{ width: undefined, height: 240 }}
          title="Description"
          value={description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
        />
      </div>
    </Dialog>
  );
}
